// Capture with the user's browser renderer. Keep native controls and the original
// CSS layout; copying computed widths or replacing inputs changes their geometry.
const XHTML = 'http://www.w3.org/1999/xhtml';
const EXPORT_VIEWPORT = {
  width: 1440,
  height: 1200,
  innerWidth: 1440,
  innerHeight: 1200,
};
const EXPORT_CARD = { width: 600, height: 900 };

function needsWebKitBackgroundLayer(view) {
  const agent = view.navigator.userAgent;
  const mobileApple = /iPhone|iPad|iPod/i.test(agent)
    || (view.navigator.platform === 'MacIntel' && view.navigator.maxTouchPoints > 1);
  return mobileApple || (/AppleWebKit/i.test(agent)
    && !/(Chrome|Chromium|Edg|OPR|SamsungBrowser)/i.test(agent));
}

function blobAsDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read a recipe image.'));
    reader.readAsDataURL(blob);
  });
}

function viewportUnits(css, view) {
  const sizes = { vw: view.innerWidth, vh: view.innerHeight,
    vmin: Math.min(view.innerWidth, view.innerHeight),
    vmax: Math.max(view.innerWidth, view.innerHeight) };
  return css.replace(/\b(\d*\.?\d+)(vw|vh|vmin|vmax)\b/g,
    (_, value, unit) => `${Number(value) * sizes[unit] / 100}px`);
}

async function snapshotStyles(doc, inlineURLs) {
  // Evaluate responsive rules in a detached desktop-sized browsing context.
  // The user's real viewport and editor DOM remain untouched.
  const frame = doc.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
  frame.style.cssText = `position:fixed;left:-${EXPORT_VIEWPORT.width + 1}px;top:0;width:${EXPORT_VIEWPORT.width}px;height:${EXPORT_VIEWPORT.height}px;border:0;visibility:hidden;pointer-events:none;`;
  doc.body.appendChild(frame);
  const view = frame.contentWindow;
  if (!view) {
    frame.remove();
    throw new Error('Could not prepare the desktop recipe-card export context.');
  }
  async function serialize(rules, base) {
    const result = [];
    for (const rule of rules) {
      // Media queries must use the editor viewport, not the export image size.
      if (rule.type === 4) {
        if (view.matchMedia(rule.conditionText).matches) {
          result.push(await serialize(rule.cssRules, base));
        }
      } else if (rule.type === 3) {
        if (!rule.media.mediaText || view.matchMedia(rule.media.mediaText).matches) {
          result.push(await serialize(rule.styleSheet.cssRules, rule.href));
        }
      } else if (rule.type === 1) {
        // Tailwind escapes viewport units inside class names; only transform
        // declarations, never selectors such as .text-\[clamp(...vw...)\].
        result.push(`${rule.selectorText}{${await inlineURLs(viewportUnits(rule.style.cssText, view), base)}}`);
      } else {
        result.push(await inlineURLs(rule.cssText, base));
      }
    }
    return result.join('\n');
  }
  try {
    const sheets = [];
    for (const sheet of doc.styleSheets) {
      if (sheet.disabled || (sheet.media.mediaText && !view.matchMedia(sheet.media.mediaText).matches)) continue;
      try {
        sheets.push(await serialize(sheet.cssRules, sheet.href || doc.baseURI));
      } catch (error) {
        // Missing CSS must fail visibly instead of downloading an inaccurate card.
        throw new Error(`Could not capture the recipe stylesheet: ${error.message}`);
      }
    }
    return sheets.join('\n');
  } finally {
    frame.remove();
  }
}

export async function captureRecipeCard(node, { scale = 3 } = {}) {
  if (!node?.isConnected) throw new Error('Recipe card is not ready to export.');
  const doc = node.ownerDocument;
  const view = doc.defaultView;
  const webKitBackgroundLayer = needsWebKitBackgroundLayer(view);
  await doc.fonts.ready;
  await new Promise(resolve => view.requestAnimationFrame(resolve));

  // Export dimensions and responsive CSS are deliberately independent of the
  // device viewport. The canonical desktop design is 600 × 900 CSS pixels.
  const rect = EXPORT_CARD;
  if (!Number.isFinite(scale) || scale <= 0) throw new Error('Invalid export resolution.');

  const assetCache = new Map();
  const inlineAsset = async (src, base = doc.baseURI) => {
    if (!src || src.startsWith('data:') || src.startsWith('#')) return src;
    const url = new URL(src, base).href;
    if (!assetCache.has(url)) {
      assetCache.set(url, (async () => {
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('A recipe image or font could not be loaded.');
        return blobAsDataURL(await response.blob());
      })());
    }
    return assetCache.get(url);
  };
  const inlineURLs = async (css, base) => {
    const pattern = /url\(\s*(['"]?)(.*?)\1\s*\)/g;
    const replacements = await Promise.all([...css.matchAll(pattern)].map(async match =>
      [match[0], `url("${await inlineAsset(match[2], base)}")`]));
    for (const [before, after] of replacements) css = css.split(before).join(after);
    return css;
  };

  let backgroundImageData = null;
  if (webKitBackgroundLayer) {
    const background = view.getComputedStyle(node).backgroundImage;
    const url = background.match(/url\(\s*(['"]?)(.*?)\1\s*\)/);
    if (url) backgroundImageData = await inlineAsset(url[2]);
  }

  // Snapshot values synchronously, before awaiting resources. Nothing is changed
  // in the editor: export must not affect React state, focus, or unsaved content.
  const clone = node.cloneNode(true);
  const sources = [node, ...node.querySelectorAll('*')];
  const copies = [clone, ...clone.querySelectorAll('*')];
  const resources = sources.map(async (source, index) => {
    const copy = copies[index];
    if (source.tagName === 'INPUT') {
      copy.setAttribute('value', source.value);
      // Placeholder copy is editor guidance, not recipe content. Remove it
      // from the detached export clone so unfilled fields render as blank.
      copy.removeAttribute('placeholder');
      if (source.checked) copy.setAttribute('checked', '');
      else copy.removeAttribute('checked');
    } else if (source.tagName === 'TEXTAREA') {
      copy.textContent = source.value;
      copy.removeAttribute('placeholder');
    } else if (source.tagName === 'SELECT') {
      [...copy.options].forEach((option, i) => {
        option.toggleAttribute('selected', source.options[i].selected);
      });
    }
    if (source.tagName === 'IMG') {
      copy.removeAttribute('srcset');
      copy.removeAttribute('loading');
      copy.src = await inlineAsset(source.currentSrc || source.src);
    }
    if (copy.hasAttribute('style')) {
      copy.setAttribute('style', await inlineURLs(viewportUnits(copy.getAttribute('style'), EXPORT_VIEWPORT), doc.baseURI));
    }
    if (copy === clone && webKitBackgroundLayer && backgroundImageData) {
      // WebKit drops CSS backgrounds on foreignObject content. Paint the
      // unchanged template asset directly onto the output canvas instead.
      copy.style.backgroundImage = 'none';
      copy.style.backgroundColor = 'transparent';
    }
  });
  const [css] = await Promise.all([snapshotStyles(doc, inlineURLs), ...resources]);

  // HTML/body carry inherited line-height and font defaults. A bare div changes
  // the signature baseline and shrinks the category/servings/time boxes.
  const html = doc.documentElement.cloneNode(false);
  html.setAttribute('xmlns', XHTML);
  html.style.cssText = `margin:0;padding:0;width:${rect.width}px;height:${rect.height}px;background:white;`;
  html.style.fontSize = view.getComputedStyle(doc.documentElement).fontSize;
  const body = doc.body.cloneNode(false);
  body.style.cssText = `margin:0;padding:0;width:${rect.width}px;height:${rect.height}px;background:white;`;
  if (webKitBackgroundLayer && backgroundImageData) {
    html.style.background = 'transparent';
    body.style.background = 'transparent';
  }
  const style = doc.createElement('style');
  style.textContent = css;
  html.append(style, body);

  clone.style.position = 'relative';
  clone.style.top = '0';
  clone.style.left = '0';
  clone.style.margin = '0';
  clone.style.width = `${rect.width}px`;
  clone.style.height = `${rect.height}px`;
  body.appendChild(clone);

  const width = Math.ceil(rect.width);
  const height = Math.ceil(rect.height);
  const outputWidth = Math.ceil(width * scale);
  const outputHeight = Math.ceil(height * scale);
  const xml = new XMLSerializer().serializeToString(html);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"><foreignObject width="100%" height="100%">${xml}</foreignObject></svg>`;
  const image = new Image();
  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  await image.decode();
  const canvas = doc.createElement('canvas');
  canvas.width = outputWidth;
  canvas.height = outputHeight;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Image export is unavailable in this browser.');
  context.scale(scale, scale);
  // The editor card sits on the page's white canvas. Keep the rounded corners
  // composited against that same surface in PNG, JPG, and the embedded PDF.
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  if (webKitBackgroundLayer && backgroundImageData) {
    const backgroundImage = new Image();
    backgroundImage.src = backgroundImageData;
    await backgroundImage.decode();

    const size = view.getComputedStyle(node).backgroundSize;
    const coverScale = size === 'cover'
      ? Math.max(width / backgroundImage.naturalWidth, height / backgroundImage.naturalHeight)
      : 1;
    const drawWidth = backgroundImage.naturalWidth * coverScale;
    const drawHeight = backgroundImage.naturalHeight * coverScale;
    const borderRadius = parseFloat(view.getComputedStyle(node).borderTopLeftRadius) || 0;

    context.save();
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    if (borderRadius > 0) {
      const radius = Math.min(borderRadius, width / 2, height / 2);
      context.beginPath();
      context.moveTo(radius, 0);
      context.lineTo(width - radius, 0);
      context.quadraticCurveTo(width, 0, width, radius);
      context.lineTo(width, height - radius);
      context.quadraticCurveTo(width, height, width - radius, height);
      context.lineTo(radius, height);
      context.quadraticCurveTo(0, height, 0, height - radius);
      context.lineTo(0, radius);
      context.quadraticCurveTo(0, 0, radius, 0);
      context.closePath();
      context.clip();
    }
    context.drawImage(backgroundImage,
      (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
    context.restore();
  }
  context.drawImage(image, 0, 0);
  return { canvas, width: rect.width, height: rect.height };
}
